import { FocusAreaConstArr } from 'libs/end-to-end/src/focus-area';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { ViewByOption } from 'libs/end-to-end/src/view-by';
import { BigTimePeriodComparison } from 'libs/end-to-end/src/big-time-period-comparison';
import { z } from 'zod';

export const CreateChatResponse = z.object({
  conversationId: z.string(),
});

export type CreateChatResponse = z.infer<typeof CreateChatResponse>;

export const ChatRoles = ['user', 'assistant', 'system'] as const;
export const ChatRole = z.enum(ChatRoles);
export type ChatRole = z.infer<typeof ChatRole>;

export const ChatMessageContentPart = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  z.object({
    type: z.literal('tool-call'),
    toolCallId: z.string(),
    name: z.string(),
    args: z.record(z.unknown()),
  }),
  z.object({
    type: z.literal('tool-result'),
    toolCallId: z.string(),
    name: z.string().optional(),
    result: z.unknown(),
  }),
]);

export type ChatMessageContentPart = z.infer<typeof ChatMessageContentPart>;

export const ChatMessage = z.object({
  id: z.string().uuid().optional(),
  role: ChatRole,
  parts: z.array(ChatMessageContentPart),
  createdAt: z.string().datetime().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type ChatMessage = z.infer<typeof ChatMessage>;

export const CreateChatRequest = z.object({
  conversationId: z.string().uuid().optional(),
  message: ChatMessage,
});

export type CreateChatRequest = z.infer<typeof CreateChatRequest>;

export const GetChatResponse = z.object({
  conversationId: z.string().uuid().optional(),
  messages: z.array(ChatMessage),
});

export type GetChatResponse = z.infer<typeof GetChatResponse>;

const FocusAreaEnum = z.enum(FocusAreaConstArr);

const BiasAnalysisToolSchema = z.object({
  customVizId: z.literal('biasAnalysis'),
  inputs: z.object({
    extras: z.object({
      filters: z.array(FilterDefinitionForExtras),
      dimension: z.string(),
      metric: z.string(),
      focusArea: FocusAreaEnum,
    }),
  }),
});

const BinScatterToolSchema = z.object({
  customVizId: z.literal('binScatterMultiSelect'),
  inputs: z.object({
    extras: z.object({
      filters: z.array(FilterDefinitionForExtras),
      dimension: z.string(),
      metric: z.string(),
      focusArea: FocusAreaEnum,
    }),
  }),
});

const GeolocationToolSchema = z.object({
  customVizId: z.literal('geolocation'),
  inputs: z.object({
    extras: z.object({
      metrics: z.array(z.string()),
      startDate: z.string(),
      endDate: z.string(),
      dateRangeType: z.enum(['all', 'custom']).nullish(),
      filters: z.array(FilterDefinitionForExtras),
    }),
  }),
});

const HeatmapToolSchema = z.object({
  customVizId: z.literal('heatmap'),
  inputs: z.object({
    extras: z.object({
      dimension: z.string(),
      filters: z.array(FilterDefinitionForExtras),
      metrics: z.array(z.string()),
    }),
  }),
});

const PipelineAnalysisToolSchema = z.object({
  customVizId: z.literal('pipelineAnalysis'),
  inputs: z.object({
    extras: z.object({
      dimension: z.string(),
      analysisName: z.string(),
      filters: z.array(FilterDefinitionForExtras),
    }),
  }),
});

const ProgressToolSchema = z.object({
  customVizId: z.literal('progress'),
  inputs: z.object({
    extras: z.object({
      dimension: z.string(),
      focusArea: FocusAreaEnum,
      metric: z.string(),
      filters: z.array(FilterDefinitionForExtras),
    }),
  }),
});

const SuccessPhenotypeToolSchema = z.object({
  customVizId: z.literal('successPhenotypeV2'),
  inputs: z.object({
    extras: z.object({
      filters: z.array(FilterDefinitionForExtras),
      metric: z.string(),
    }),
  }),
});

const TableViewToolSchema = z.object({
  customVizId: z.literal('tableView'),
  inputs: z.object({
    extras: z.object({
      groupBy: z.string().nullable(),
      metrics: z.array(z.string()),
      filters: z.array(FilterDefinitionForExtras),
    }),
  }),
});

const TimeSeriesTrendsToolSchema = z.object({
  customVizId: z.literal('timeSeriesTrends'),
  inputs: z.object({
    extras: z.object({
      filters: z.array(FilterDefinitionForExtras),
      metric: z.string(),
      breakdownOption: z.string().nullable(),
      timeBoiOption: ViewByOption,
      bigTimeComparisonOption: BigTimePeriodComparison,
      startDate: z.string(),
      endDate: z.string(),
      dateRangeType: z.string().nullish(),
      chartType: z.string().nullish(),
      treatmentDate: z.string().nullish(),
      treatmentGroup: z.string().nullish(),
    }),
  }),
});

const SummaryStatsToolSchema = z.object({
  customVizId: z.literal('summaryStats'),
  inputs: z.object({
    extras: z.object({
      metrics: z.array(z.string()),
      filters: z.array(FilterDefinitionForExtras),
    }),
  }),
});

const SupportedCustomVizToolSchemas = z.discriminatedUnion('customVizId', [
  BiasAnalysisToolSchema,
  BinScatterToolSchema,
  GeolocationToolSchema,
  HeatmapToolSchema,
  PipelineAnalysisToolSchema,
  ProgressToolSchema,
  SuccessPhenotypeToolSchema,
  TableViewToolSchema,
  TimeSeriesTrendsToolSchema,
  SummaryStatsToolSchema,
]);

export const StartCustomVizArgs = z.object({
  request: SupportedCustomVizToolSchemas,
});

export type StartCustomVizArgs = z.infer<typeof StartCustomVizArgs>;
